// src/utils/lexileEngine.js
// -----------------------------------------------------------------------------
// King's Academy Faculty Tool Kit — text leveling engine
//
// Owns everything between "teacher clicked Level this text" and "here is the
// leveled text": chunking, prompt construction, the Anthropic call, response
// parsing, and the closed-loop revision cycle that measures Claude's output
// locally and sends it back for another pass if it missed the target.
// -----------------------------------------------------------------------------

import { getAuth } from 'firebase/auth';
import {
  analyzeText,
  lexileToGradeLabel,
  sentenceLengthWindow,
  countWords,
} from './readability';

// -----------------------------------------------------------------------------
// CONFIG — the only block you should need to touch
// -----------------------------------------------------------------------------

// TODO: move this to Firestore config/anthropic so model migrations don't need a
// redeploy. Read once at app start and pass in via context.
export const CLAUDE_MODEL = 'claude-sonnet-4-6';

export const ANTHROPIC_PROXY_URL =
  'https://us-central1-kings-app-74847.cloudfunctions.net/anthropicProxy';

// Words per chunk. Long articles are split on paragraph boundaries so no
// single request runs up against the output token ceiling.
const CHUNK_TARGET_WORDS = 900;

// How close is close enough, in Lexile points. One Lexile standard error is 60L.
const TOLERANCE = 75;

// Revision attempts per chunk, including the first pass.
const MAX_ATTEMPTS = 3;

// -----------------------------------------------------------------------------
// Anthropic proxy call
// -----------------------------------------------------------------------------

/**
 * Calls the anthropicProxy Cloud Function.
 *
 * NOTE: adjust the request body if your anthropicProxy expects a different
 * shape. This assumes it forwards { model, max_tokens, system, messages }
 * straight through to the Messages API and returns the raw API response.
 */
export async function callClaude({ system, messages, maxTokens = 8000 }) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('You are signed out. Sign in again and retry.');
  const idToken = await user.getIdToken();

  const response = await fetch(ANTHROPIC_PROXY_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      model: CLAUDE_MODEL,
      max_tokens: maxTokens,
      system,
      messages,
    }),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`The AI service returned ${response.status}. ${detail.slice(0, 200)}`);
  }

  const data = await response.json();
  const content = data?.content;
  if (!Array.isArray(content)) {
    throw new Error('The AI service returned an unexpected response shape.');
  }

  return content
    .filter((block) => block.type === 'text')
    .map((block) => block.text)
    .join('\n')
    .trim();
}

// -----------------------------------------------------------------------------
// Chunking
// -----------------------------------------------------------------------------

export function chunkByParagraph(text, targetWords = CHUNK_TARGET_WORDS) {
  const paragraphs = String(text || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return [];

  const chunks = [];
  let current = [];
  let currentWords = 0;

  paragraphs.forEach((para) => {
    const w = countWords(para);
    if (currentWords > 0 && currentWords + w > targetWords) {
      chunks.push(current.join('\n\n'));
      current = [];
      currentWords = 0;
    }
    current.push(para);
    currentWords += w;
  });

  if (current.length) chunks.push(current.join('\n\n'));
  return chunks;
}

// -----------------------------------------------------------------------------
// Prompts
// -----------------------------------------------------------------------------

const SYSTEM_PROMPT = `You adapt reading material for multilingual and English-language-learner students at King's Academy, an international boarding school in Madaba, Jordan.

Your job is to change how hard a text is to read without changing what it says. Teachers put your output side by side with the original in the same classroom, so the two versions must line up paragraph for paragraph.

You are precise about reading level. Difficulty is driven by two things: sentence length and word frequency. You control both deliberately rather than guessing.

You never write down to students. A sixteen-year-old reading at a fourth-grade level is still a sixteen-year-old: the sentences get shorter and the words get more common, but the tone stays serious and the ideas stay intact.`;

function formatProtectedTerms(terms) {
  if (!terms || terms.length === 0) return null;
  return terms.map((t) => `  - ${t}`).join('\n');
}

/**
 * Builds the instruction block shared by first pass and revisions.
 */
function buildInstructions({
  targetLexile,
  sourceAnalysis,
  protectedTerms,
  includeGlossary,
  glossaryLanguage,
  chunkIndex,
  chunkCount,
}) {
  const gradeLabel = lexileToGradeLabel(targetLexile);
  const delta = targetLexile - sourceAnalysis.lexile;
  const direction = delta < 0 ? 'easier' : 'harder';
  const window = sentenceLengthWindow(targetLexile, sourceAnalysis.stats.meanZipf);

  const lines = [];

  lines.push(`TARGET: ${targetLexile}L (about ${gradeLabel})`);
  lines.push(`The full source text currently measures about ${sourceAnalysis.lexile}L (${sourceAnalysis.gradeLabel}), with a mean sentence length of ${sourceAnalysis.stats.meanSentenceLength} words. You need to make it roughly ${Math.abs(delta)}L ${direction}.`);
  lines.push('');
  lines.push('STRUCTURAL TARGETS');
  lines.push(`  - Mean sentence length: aim for ${window.min}–${window.max} words per sentence.`);
  if (delta < 0) {
    lines.push('  - Vocabulary: replace low-frequency words with everyday equivalents wherever meaning allows.');
    lines.push('  - Vary your sentence lengths around that mean. A page of identical short sentences reads as a list and is genuinely harder to follow, not easier.');
  } else {
    lines.push('  - Vocabulary: use more precise, lower-frequency words where they carry real meaning. Do not inflate the language for its own sake.');
  }
  lines.push('');
  lines.push('RULES');
  lines.push('  1. Keep every fact, claim, name, number, date, and statistic. Do not summarise, condense, or drop paragraphs. The output may be longer than the input; that is fine.');
  lines.push('  2. Keep the same number of paragraphs, in the same order, covering the same content. Teachers compare the two versions line by line.');
  lines.push('  3. Reproduce direct quotations exactly as written. Never simplify words inside quotation marks. If a quotation is hard, leave it alone and explain it in the glossary instead.');
  lines.push('  4. Keep the tone appropriate to the students\' actual age, not their reading level.');
  lines.push('  5. Output only the adapted text. No preamble, no headings that were not in the original, no notes about what you changed.');

  const protectedBlock = formatProtectedTerms(protectedTerms);
  if (protectedBlock) {
    lines.push('');
    lines.push('TERMS THAT MUST SURVIVE');
    lines.push('The teacher requires these exact words and concepts in the output. Keep them verbatim. Do not swap them for easier synonyms — students need to own this vocabulary. Instead, make the sentences around them shorter and plainer so the terms are supported by easy context.');
    lines.push(protectedBlock);
  }

  if (chunkCount > 1) {
    lines.push('');
    lines.push(`SECTION ${chunkIndex + 1} OF ${chunkCount}`);
    lines.push('This is one section of a longer article. Do not add an introduction or a conclusion. Do not recap earlier sections. Begin and end exactly where the source section does.');
  }

  lines.push('');
  if (includeGlossary) {
    lines.push('OUTPUT FORMAT');
    lines.push('Respond with exactly this structure and nothing else:');
    lines.push('');
    lines.push('###LEVELED###');
    lines.push('(the adapted text)');
    lines.push('###GLOSSARY###');
    lines.push('term | definition');
    lines.push('term | definition');
    lines.push('');
    lines.push('GLOSSARY RULES');
    lines.push('  - Write 4–10 entries for this section. Fewer if the section is short.');
    lines.push('  - Write each definition AT THE TARGET READING LEVEL. A glossary written above the level of the text is useless.');
    lines.push('  - Give the meaning the word carries IN THIS TEXT, not every meaning it has.');
    lines.push('  - Prioritise, in this order: (a) required terms listed above, (b) idioms and figurative language, (c) culturally specific references a student outside that culture would miss, (d) any remaining uncommon words.');
    lines.push('  - Idioms and cultural references matter most. A learner can look up "photosynthesis"; they cannot look up "cut corners" or "the Rust Belt" and get a usable answer.');
    lines.push('  - Do not include the pipe character in a definition.');
    lines.push('  - Do not gloss words that are already easy.');
  } else {
    lines.push('OUTPUT FORMAT');
    lines.push('Respond with exactly this structure and nothing else:');
    lines.push('');
    lines.push('###LEVELED###');
    lines.push('(the adapted text)');
  }

  return lines.join('\n');
}

function buildFirstPassMessage(params) {
  return `${buildInstructions(params)}

SOURCE TEXT
---
${params.chunkText}
---`;
}

/**
 * Revision message. Gives Claude the measured result and the specific sentences
 * and words responsible, rather than just repeating the target number.
 */
function buildRevisionMessage({ previousOutput, measured, targetLexile, protectedTerms, includeGlossary }) {
  const delta = measured.lexile - targetLexile;
  const tooHard = delta > 0;
  const protectedSet = new Set(
    (protectedTerms || []).flatMap((t) => t.toLowerCase().split(/\s+/))
  );

  const offenders = measured.rarestWords
    .filter((r) => !protectedSet.has(r.word.toLowerCase()))
    .slice(0, 10)
    .map((r) => r.word);

  const longSentences = measured.longestSentences
    .filter((s) => s.length > measured.stats.meanSentenceLength)
    .slice(0, 3);

  const lines = [];
  lines.push(`Your draft measures ${measured.lexile}L. The target is ${targetLexile}L — you are ${Math.abs(delta)}L ${tooHard ? 'too hard' : 'too easy'}.`);
  lines.push('');
  lines.push(`Mean sentence length in your draft: ${measured.stats.meanSentenceLength} words.`);

  if (tooHard) {
    lines.push('');
    lines.push('Revise the draft below. Specifically:');
    if (longSentences.length) {
      lines.push('  - These sentences are the longest. Break each into two or three:');
      longSentences.forEach((s) => lines.push(`      (${s.length} words) ${s.text}`));
    }
    if (offenders.length) {
      lines.push(`  - These words are still uncommon. Replace them with everyday equivalents unless the meaning depends on them: ${offenders.join(', ')}`);
    }
  } else {
    lines.push('');
    lines.push('Revise the draft below. You have oversimplified. Specifically:');
    lines.push('  - Combine some short sentences using connectives such as because, although, while, and since.');
    lines.push('  - Restore precision where you flattened it. Use the specific word rather than the vague one.');
    lines.push('  - Do not add new content to pad the length.');
  }

  lines.push('');
  lines.push('All original rules still apply: keep every fact, keep the paragraph structure, keep required terms verbatim, keep quotations exact.');
  lines.push('');
  lines.push(includeGlossary
    ? 'Return the full revised text in the same ###LEVELED### / ###GLOSSARY### format.'
    : 'Return the full revised text in the same ###LEVELED### format.');
  lines.push('');
  lines.push('DRAFT TO REVISE');
  lines.push('---');
  lines.push(previousOutput);
  lines.push('---');

  return lines.join('\n');
}

// -----------------------------------------------------------------------------
// Response parsing
// -----------------------------------------------------------------------------

export function parseResponse(raw) {
  const text = String(raw || '');
  const leveledMarker = text.indexOf('###LEVELED###');
  const glossaryMarker = text.indexOf('###GLOSSARY###');

  let body;
  let glossaryBlock = '';

  if (leveledMarker === -1) {
    // Model ignored the format. Salvage what we can.
    body = glossaryMarker === -1 ? text : text.slice(0, glossaryMarker);
    if (glossaryMarker !== -1) glossaryBlock = text.slice(glossaryMarker + 14);
  } else {
    const start = leveledMarker + 13;
    if (glossaryMarker > leveledMarker) {
      body = text.slice(start, glossaryMarker);
      glossaryBlock = text.slice(glossaryMarker + 14);
    } else {
      body = text.slice(start);
    }
  }

  const glossary = glossaryBlock
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.includes('|'))
    .map((line) => {
      const pipe = line.indexOf('|');
      return {
        term: line.slice(0, pipe).trim().replace(/^[-*•]\s*/, ''),
        definition: line.slice(pipe + 1).trim(),
      };
    })
    .filter((entry) => entry.term && entry.definition);

  return { text: body.trim(), glossary };
}

function mergeGlossaries(lists) {
  const seen = new Map();
  lists.flat().forEach((entry) => {
    const key = entry.term.toLowerCase();
    if (!seen.has(key)) seen.set(key, entry);
  });
  return [...seen.values()].sort((a, b) =>
    a.term.localeCompare(b.term, undefined, { sensitivity: 'base' })
  );
}

// -----------------------------------------------------------------------------
// Main entry point
// -----------------------------------------------------------------------------

/**
 * Level a passage to a target reading level.
 *
 * @param {object} params
 * @param {string} params.sourceText
 * @param {number} params.targetLexile
 * @param {string[]} [params.protectedTerms]
 * @param {boolean} [params.includeGlossary]
 * @param {function} [params.onProgress] ({ stage, chunk, chunkCount, attempt, measured })
 * @returns {Promise<{text, glossary, analysis, sourceAnalysis, log}>}
 */
export async function levelText({
  sourceText,
  targetLexile,
  protectedTerms = [],
  includeGlossary = true,
  onProgress = () => {},
}) {
  const sourceAnalysis = analyzeText(sourceText, { protectedTerms });
  if (!sourceAnalysis) {
    throw new Error('That passage is too short to measure. Paste at least 25 words.');
  }

  const chunks = chunkByParagraph(sourceText);
  const outputs = [];
  const glossaries = [];
  const log = [];

  for (let i = 0; i < chunks.length; i++) {
    const chunkText = chunks[i];
    let messages = [
      {
        role: 'user',
        content: buildFirstPassMessage({
          chunkText,
          targetLexile,
          sourceAnalysis,
          protectedTerms,
          includeGlossary,
          chunkIndex: i,
          chunkCount: chunks.length,
        }),
      },
    ];

    let best = null;
    let bestDistance = Infinity;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      onProgress({ stage: 'generating', chunk: i + 1, chunkCount: chunks.length, attempt });

      const raw = await callClaude({ system: SYSTEM_PROMPT, messages });
      const parsed = parseResponse(raw);

      if (!parsed.text) {
        throw new Error('The AI service returned an empty result. Try again.');
      }

      const measured = analyzeText(parsed.text, { protectedTerms });
      const distance = measured ? Math.abs(measured.lexile - targetLexile) : Infinity;

      onProgress({
        stage: 'measured',
        chunk: i + 1,
        chunkCount: chunks.length,
        attempt,
        measured,
      });

      log.push({
        chunk: i + 1,
        attempt,
        lexile: measured ? measured.lexile : null,
        meanSentenceLength: measured ? measured.stats.meanSentenceLength : null,
      });

      if (distance < bestDistance) {
        best = parsed;
        bestDistance = distance;
      }

      // Too short to measure reliably, or close enough — stop here.
      if (!measured || distance <= TOLERANCE) break;
      if (attempt === MAX_ATTEMPTS) break;

      messages = [
        ...messages,
        { role: 'assistant', content: raw },
        {
          role: 'user',
          content: buildRevisionMessage({
            previousOutput: parsed.text,
            measured,
            targetLexile,
            protectedTerms,
            includeGlossary,
          }),
        },
      ];
    }

    outputs.push(best.text);
    if (includeGlossary) glossaries.push(best.glossary);
  }

  const finalText = outputs.join('\n\n');
  const analysis = analyzeText(finalText, { protectedTerms });

  onProgress({ stage: 'done', measured: analysis });

  return {
    text: finalText,
    glossary: includeGlossary ? mergeGlossaries(glossaries) : [],
    analysis,
    sourceAnalysis,
    log,
  };
}

/**
 * Check which required terms actually survived. Surfaced in the UI so a teacher
 * doesn't have to proofread for it.
 */
export function verifyProtectedTerms(text, protectedTerms) {
  const haystack = String(text || '').toLowerCase();
  return (protectedTerms || []).map((term) => ({
    term,
    present: haystack.includes(term.toLowerCase()),
  }));
}
